import React, { useEffect, useState, useCallback } from "react";
import axiosInstance from "../../../interceptors";
import { baseUrl } from "../../../config/config";

const STATUS_COLORS = {
  PENDING:    "bg-amber-100 text-amber-700",
  PROCESSING: "bg-blue-100 text-blue-700",
  COMPLETED:  "bg-emerald-100 text-emerald-700",
  DENIED:     "bg-red-100 text-red-700",
};

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

export default function DataRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: "", requestType: "DELETION", notes: "" });
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    axiosInstance
      .get(`${baseUrl}/job-posting/data-deletion/requests`)
      .then((r) => setRequests(Array.isArray(r.data) ? r.data : []))
      .catch(() => setRequests([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    document.title = "Privacy requests - Ample Hire";
    load();
  }, [load]);

  const create = async () => {
    if (!form.email.trim()) return;
    await axiosInstance.post(`${baseUrl}/job-posting/data-deletion/request`, form);
    setForm({ email: "", requestType: "DELETION", notes: "" });
    setShowCreate(false);
    load();
  };

  const execute = async (id) => {
    if (!window.confirm("Mark this request COMPLETED? (Stub: does not actually delete data — wire to a hardened deletion worker before production.)")) return;
    setBusy(id);
    try {
      await axiosInstance.post(`${baseUrl}/job-posting/data-deletion/${id}/execute`);
      load();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="page-enter">
      <div className="flex items-end justify-between mb-5">
        <div>
          <h1 className="text-2xl font-semibold text-vf-base">Privacy requests</h1>
          <p className="text-sm text-vf-muted mt-1">
            Candidate requests for data deletion or export. Tracks GDPR/DPDP/BIPA-style asks.
          </p>
        </div>
        <button
          onClick={() => setShowCreate((s) => !s)}
          className="px-3 py-1.5 rounded bg-vf-primary text-white text-xs"
        >
          {showCreate ? "Cancel" : "+ Log new request"}
        </button>
      </div>

      {showCreate && (
        <div className="mb-4 bg-white border border-slate-200 rounded-xl p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="candidate@example.com"
              className="px-3 py-2 border border-slate-300 rounded text-sm"
            />
            <select
              value={form.requestType}
              onChange={(e) => setForm({ ...form, requestType: e.target.value })}
              className="px-3 py-2 border border-slate-300 rounded text-sm"
            >
              <option value="DELETION">Deletion</option>
              <option value="EXPORT">Export</option>
              <option value="ACCESS">Access</option>
            </select>
            <button
              onClick={create}
              className="px-3 py-2 rounded bg-vf-primary text-white text-sm"
            >
              Create request
            </button>
          </div>
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            placeholder="Notes (optional)"
            className="mt-3 w-full px-3 py-2 border border-slate-300 rounded text-sm resize-none"
            rows={2}
          />
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-vf-muted">
            <tr>
              <th className="text-left px-4 py-2 font-medium">ID</th>
              <th className="text-left px-4 py-2 font-medium">Email</th>
              <th className="text-left px-4 py-2 font-medium">Type</th>
              <th className="text-left px-4 py-2 font-medium">Status</th>
              <th className="text-left px-4 py-2 font-medium">Requested</th>
              <th className="text-left px-4 py-2 font-medium">Completed</th>
              <th className="text-right px-4 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-vf-muted text-xs">Loading…</td></tr>
            )}
            {!loading && requests.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-vf-muted text-xs">No requests yet.</td></tr>
            )}
            {requests.map((r) => (
              <tr key={r.id}>
                <td className="px-4 py-2 text-vf-muted">#{r.id}</td>
                <td className="px-4 py-2 text-vf-base">{r.email}</td>
                <td className="px-4 py-2 text-vf-base">{r.requestType}</td>
                <td className="px-4 py-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[r.status] || "bg-slate-100 text-slate-700"}`}>
                    {r.status}
                  </span>
                </td>
                <td className="px-4 py-2 text-vf-muted text-xs">{fmt(r.requestedAt)}</td>
                <td className="px-4 py-2 text-vf-muted text-xs">{fmt(r.completedAt)}</td>
                <td className="px-4 py-2 text-right">
                  {r.status === "PENDING" && (
                    <button
                      disabled={busy === r.id}
                      onClick={() => execute(r.id)}
                      className="px-2 py-1 rounded border border-slate-300 text-xs hover:bg-slate-50 disabled:opacity-50"
                    >
                      {busy === r.id ? "Working…" : "Mark complete"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 text-[11px] text-vf-muted">
        ⚠️ The "Mark complete" action currently logs the action but does NOT delete underlying data.
        Wire this to a hardened deletion worker (S3 + multi-table) before production.
      </div>
    </div>
  );
}
